/* =========================================================================
   census.ts — ABS Census 2021 data integration for catchment analysis
   ========================================================================= */

import censusSAL from "./data/census-north-ryde-sal.json";
import censusPOA from "./data/census-postcode-2113.json";
import type { Scenario } from "./types";

export interface CensusArea {
  area_code: string;
  area_type: string;
  area_name: string;
  census_year: number;
  total_population: number | null;
  male_pct: number | null;
  female_pct: number | null;
  median_age: number | null;
  total_families: number | null;
  avg_children_per_family: number | null;
  total_dwellings: number | null;
  occupied_dwellings: number | null;
  avg_people_per_household: number | null;
  median_weekly_household_income: number | null;
  median_weekly_rent: number | null;
  median_monthly_mortgage: number | null;
  family_households: number | null;
  single_person_households: number | null;
  group_households: number | null;
  owned_outright: number | null;
  owned_with_mortgage: number | null;
  rented: number | null;
  preschool_attendees: number | null;
  primary_total: number | null;
  secondary_total: number | null;
  tertiary_total: number | null;
  school_age_children_estimate: number | null;
  annual_median_household_income: number | null;
  // ---- education-by-sector (ABS G15; optional — present on catchments fetched via scripts/fetch-abs-census.ts) ----
  /** Primary students at "Other non-Government" (independent) schools — ABS G15 TYSTAP=23 */
  primary_other_nongovt?: number | null;
  /** Secondary students at "Other non-Government" (independent) schools — ABS G15 TYSTAP=33 */
  secondary_other_nongovt?: number | null;
  /** Independent-school propensity = (primary+secondary other-non-Govt) / (primary+secondary attending). Layer-2 demand input. */
  independent_school_share?: number | null;
}

/**
 * NSW average independent-school ("Other non-Government") share of primary+secondary attendees, ABS 2021.
 * Normalising constant for `marketDemandIndex`. It only scales the index uniformly, so cross-market
 * ORDERING is robust to its exact value; refine by fetching G15 at REGION=1 (NSW) if precision matters.
 */
export const NSW_AVG_INDEPENDENT_SHARE = 0.14;

/** Independent-school propensity for a catchment (0–1), or null if the sector data is absent. */
export function independentSchoolShare(census: CensusArea): number | null {
  if (typeof census.independent_school_share === "number")
    return census.independent_school_share;
  const p = census.primary_other_nongovt;
  const s = census.secondary_other_nongovt;
  const denom = (census.primary_total ?? 0) + (census.secondary_total ?? 0);
  if (p == null || s == null || denom <= 0) return null;
  return (p + s) / denom;
}

/**
 * Layer-2 market-demand index (ported from the lyceum MISOCP demand model, `papers/lyceum/hardening.py`):
 * `school_age_children × (independent_share / NSW_avg_share)` — weights raw school-age population by the
 * catchment's propensity to choose independent schooling vs the NSW average. A relative "private-school
 * demand strength" score for comparing markets. DISPLAY-ONLY (per decision D4) — it does NOT feed `compute()`.
 * Returns null if school-age or sector data is missing.
 */
export function marketDemandIndex(
  census: CensusArea,
  nswAvgShare: number = NSW_AVG_INDEPENDENT_SHARE,
): number | null {
  const children = census.school_age_children_estimate;
  const share = independentSchoolShare(census);
  if (children == null || share == null || nswAvgShare <= 0) return null;
  return Math.round(children * (share / nswAvgShare));
}

/** North Ryde Suburb and Locality (SAL13015) — core catchment */
export const NORTH_RYDE_SAL: CensusArea = censusSAL as CensusArea;

/** Postcode 2113 — broader catchment including Macquarie Park */
export const POSTCODE_2113: CensusArea = censusPOA as CensusArea;

/** QuickStats snapshot for the model's default catchment */
export const DEFAULT_CATCHMENT = NORTH_RYDE_SAL;

/** Bundled markets the config picker offers. Keyed by area_code (stable ABS id). */
export const MARKET_REGISTRY: ReadonlyArray<CensusArea> = [
  NORTH_RYDE_SAL,
  POSTCODE_2113,
];

/** Look a bundled market up by its area_code (for the picker round-trip). */
export function marketByCode(code: string | undefined): CensusArea | undefined {
  if (!code) return undefined;
  return MARKET_REGISTRY.find((m) => m.area_code === code);
}

/** Resolve the catchment for a scenario with the FOUNDATION-frozen precedence: code → blob →
 *  default. Implements the FOUNDATION `CatchmentResolver` contract. */
export function catchmentFor(
  scenario: Pick<Scenario, "catchment"> & {
    assumptions?: Pick<Scenario["assumptions"], "catchmentCode">;
  },
): CensusArea {
  return (
    marketByCode(scenario.assumptions?.catchmentCode) ?? // 1. picker write-target (registry code)
    scenario.catchment ?? // 2. self-contained embedded snapshot
    DEFAULT_CATCHMENT
  ); // 3. static North Ryde fallback
}

const CATCHMENT_CACHE_KEY_PREFIX = "sf:census:";

/** Read a cached CensusArea from localStorage by area_code. */
export function getCachedCatchment(code: string): CensusArea | undefined {
  try {
    const raw = localStorage.getItem(`${CATCHMENT_CACHE_KEY_PREFIX}${code}`);
    if (!raw) return undefined;
    return JSON.parse(raw) as CensusArea;
  } catch {
    return undefined;
  }
}

/** Write a CensusArea to localStorage keyed by its area_code. */
export function setCachedCatchment(area: CensusArea): void {
  try {
    localStorage.setItem(
      `${CATCHMENT_CACHE_KEY_PREFIX}${area.area_code}`,
      JSON.stringify(area),
    );
  } catch {
    // ignore storage quota / private-mode errors
  }
}

/** Convert an Australian postcode to an ABS POA area code. */
export function postcodeToAreaCode(postcode: string): string | null {
  const digits = postcode.replace(/\D/g, "");
  if (!/^\d{4}$/.test(digits)) return null;
  return `POA${digits}`;
}

/** Convert an ABS POA area code back to a 4-digit postcode. */
export function areaCodeToPostcode(code: string | undefined): string | null {
  if (!code) return null;
  const m = code.match(/^POA(\d{4})$/);
  return m ? m[1] : null;
}

/**
 * Affordability score: compares median household income to a target annual fee.
 * Returns a ratio (1.0 = income exactly matches fee, >1 = affordable, <1 = stretch).
 */
export function affordabilityRatio(
  census: CensusArea,
  annualFee: number,
): number | null {
  const income = census.annual_median_household_income;
  if (!income || !annualFee || annualFee <= 0) return null;
  return income / annualFee;
}

/**
 * Estimates potential enrolment pool from school-age children.
 * Applies a capture rate (default 15% for private schools in competitive markets).
 */
export function estimatedEnrolmentPool(
  census: CensusArea,
  captureRate: number = 0.15,
): number | null {
  const children = census.school_age_children_estimate;
  if (!children) return null;
  return Math.round(children * captureRate);
}
